import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Icon,
  IconBtn,
  Spinner,
  Tabs,
  useToast,
  useConfirm,
} from "../../../design-system";
import {
  getAbsences,
  getEmployee,
  inviteEmployee,
  getEmployeeAbsenceBalance,
  getEmployeeAbsenceAdjustments,
  deleteEmployeeAbsenceAdjustment,
  getEmployeePersonalDetails,
  getEmployeeActiveContract,
  removeEmployeeAvatar,
  setEmployeeAvatar,
  getWorkSchedules,
  listSpecialSchedules,
  deleteSpecialSchedule,
} from "../../../helpers/kronaly_helper";
import { employeeAvatarPath } from "../../../helpers/url_helper";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import AvatarUploader from "../Avatares/AvatarUploader";
import PersonalDetailsModal from "./PersonalDetailsModal";
import NewAbsenceModal from "../Ausencias/NewAbsenceModal";
import AddFreeDaysModal from "../Ausencias/AddFreeDaysModal";
import EmployeeDocumentsTab from "./EmployeeDocumentsTab";
import TabContratos from "../Contratos/TabContratos";
import AsignarEspecialModal from "../../../pages/Jornada/Especiales/SolicitarEspecialModal";
import {
  ABSENCE_STATUS_LABEL,
  ABSENCE_STATUS_TONE,
  ABSENCE_TYPE_LABEL,
  ABSENCE_TYPE_TONE,
  formatDays as formatAbsenceDays,
  formatRange as formatAbsenceRange,
} from "../Ausencias/absenceHelpers";

const MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const formatYmd = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

function EmpleadoDetalleDrawer({ empleado, open, onClose }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { employeeProfileId: myEmployeeId, can, isManager } = useCurrentMembership();
  const canManage = can("employees:manage") || isManager;
  const [tab, setTab] = useState("perfil");
  const [profile, setProfile] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [inviting, setInviting] = useState(false);

  const lastEmpleadoRef = useRef(empleado);
  if (empleado) lastEmpleadoRef.current = empleado;
  const display = empleado || lastEmpleadoRef.current;

  const employeeId = empleado?.id || null;

  const goToCreateContract = () => {
    if (!employeeId) return;
    onClose();
    navigate("/contratos", { state: { openContractFor: employeeId } });
  };

  const handleInvite = async () => {
    if (!employeeId || inviting) return;
    setInviting(true);
    try {
      await inviteEmployee(employeeId);
      // Reflejamos el nuevo estado localmente para que el botón pase a
      // "Reenviar invitación" sin recargar toda la ficha.
      setProfile((prev) =>
        prev ? { ...prev, has_pending_invitation: true } : prev,
      );
      toast.success(
        "Invitación enviada",
        `Hemos enviado un email a ${profile?.email || "el empleado"} para que active su cuenta y pueda acceder.`,
      );
    } catch (e) {
      toast.error(
        "No se pudo enviar la invitación",
        typeof e === "string" ? e : e?.message || "Inténtalo de nuevo.",
      );
    } finally {
      setInviting(false);
    }
  };

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [empRes, pdRes, ctRes] = await Promise.all([
        getEmployee(id).catch(() => ({ data: null })),
        getEmployeePersonalDetails(id).catch(() => ({ data: null })),
        getEmployeeActiveContract(id).catch(() => ({ data: null })),
      ]);
      setProfile(empRes?.data ?? null);
      setPersonal(pdRes?.data ?? null);
      setContract(ctRes?.data ?? null);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar la información del empleado.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !employeeId) return;
    setTab("perfil");
    load(employeeId);
  }, [open, employeeId, load]);

  if (!display) return null;

  const hasContract = Boolean(contract?.contract);
  const terms = contract?.current_terms || null;

  const displayName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || display.name
    : display.name;
  const displayStatus = profile?.status || display.status;
  const displayRole = terms?.job_title || profile?.position || display.role || null;

  const title = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 12,
        color: "var(--kr-fg-muted)",
      }}
    >
      <span>Empleados</span>
      <span style={{ color: "var(--kr-fg-subtle)" }}>/</span>
      <span style={{ color: "var(--kr-fg-strong)", fontWeight: 600 }}>
        {displayName}
      </span>
    </div>
  );

  return (
    <Drawer open={open} onClose={onClose} size="xl" title={title}>
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Header
          name={displayName}
          status={displayStatus}
          role={displayRole}
          terms={terms}
          contract={contract?.contract}
          employeeId={employeeId}
          hasAvatar={!!profile?.has_avatar}
          canEditAvatar={canManage || (myEmployeeId && myEmployeeId === employeeId)}
          onAvatarChanged={(hasAvatar) =>
            setProfile((prev) => (prev ? { ...prev, has_avatar: hasAvatar } : prev))
          }
          canInvite={canManage}
          userStatus={profile?.user_status || null}
          hasPendingInvite={!!profile?.has_pending_invitation}
          inviting={inviting}
          onInvite={handleInvite}
        />

        <div
          style={{
            marginTop: 16,
            borderBottom: "1px solid var(--kr-border-subtle)",
          }}
        >
          <Tabs
            tabs={[
              { id: "perfil", label: "Perfil" },
              { id: "horario", label: "Horario y jornada" },
              { id: "ausencias", label: "Ausencias" },
              { id: "documentos", label: "Documentos" },
              { id: "contrato", label: "Contrato" },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>

        <div
          style={{
            paddingTop: 20,
            background: "var(--kr-neutral-50)",
            margin: "0 -20px -20px",
            padding: "20px",
          }}
        >
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          {loading ? (
            <Card style={{ textAlign: "center", padding: 40 }}>
              <Spinner size={20} />
            </Card>
          ) : (
            <>
              {tab === "perfil" && (
                <PerfilTab
                  profile={profile || display}
                  personal={personal}
                  hasContract={hasContract}
                  terms={terms}
                  onCompletePersonal={() => setShowPersonalModal(true)}
                  onCreateContract={goToCreateContract}
                />
              )}
              {tab === "horario" && (
                <NoContractTab
                  hasContract={hasContract}
                  onCreateContract={goToCreateContract}
                  contentTitle="Horario y jornada"
                  Content={() => <HorarioTab employeeProfileId={employeeId} terms={terms} />}
                />
              )}
              {tab === "ausencias" && (
                <NoContractTab
                  hasContract={hasContract}
                  onCreateContract={goToCreateContract}
                  contentTitle="Ausencias"
                  Content={() => <AusenciasTab employeeProfileId={employeeId} />}
                />
              )}
              {tab === "documentos" && <EmployeeDocumentsTab empleado={display} />}
              {tab === "contrato" && (
                <NoContractTab
                  hasContract={hasContract}
                  onCreateContract={goToCreateContract}
                  contentTitle="Contrato"
                  Content={() => (
                    <ContratoTab
                      contract={contract?.contract}
                      terms={terms}
                    />
                  )}
                />
              )}
            </>
          )}
        </div>
      </div>

      <PersonalDetailsModal
        open={showPersonalModal}
        employeeProfileId={employeeId}
        onClose={() => setShowPersonalModal(false)}
        onSaved={(saved) => {
          if (saved?.personal) setPersonal(saved.personal);
          if (employeeId) load(employeeId);
        }}
      />

    </Drawer>
  );
}

function Header({
  name,
  status,
  role,
  terms,
  contract,
  employeeId,
  hasAvatar,
  canEditAvatar,
  onAvatarChanged,
  canInvite,
  userStatus,
  hasPendingInvite,
  inviting,
  onInvite,
}) {
  // La cuenta de acceso puede estar activada, invitada (pendiente de fijar
  // contraseña) o aún sin invitar. Sólo ofrecemos invitar cuando no está
  // activada y quien mira puede gestionar empleados.
  const accountActive = userStatus === "activated";
  const showInvite = canInvite && !!employeeId && userStatus && !accountActive;
  const startDate = contract?.start_date ? formatYmd(contract.start_date) : null;
  return (
    <div
      style={{
        paddingBottom: 16,
        borderBottom: "1px solid var(--kr-border-subtle)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        {employeeId ? (
          <AvatarUploader
            name={name || "?"}
            hasAvatar={hasAvatar}
            avatarUrl={employeeAvatarPath(employeeId)}
            canEdit={canEditAvatar}
            onSet={async (file) => {
              await setEmployeeAvatar(employeeId, file);
              onAvatarChanged?.(true);
            }}
            onRemove={async () => {
              await removeEmployeeAvatar(employeeId);
              onAvatarChanged?.(false);
            }}
            size={64}
          />
        ) : (
          <Avatar name={name} size={64} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--kr-font-display)",
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "var(--kr-fg-strong)",
              }}
            >
              {name}
            </h2>
            <StatusBadge status={status} />
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--kr-fg-muted)",
              marginTop: 4,
            }}
          >
            {role || "Sin cargo"}
          </div>
          {employeeId && (
            <div
              style={{
                fontSize: 10,
                color: "var(--kr-fg-subtle)",
                marginTop: 2,
                fontFamily: "monospace",
                cursor: "pointer",
                opacity: 0.5,
              }}
              title="Copiar ID"
              onClick={() => navigator.clipboard.writeText(employeeId)}
            >
              {employeeId}
            </div>
          )}
          {(terms || startDate) && (
            <div
              style={{
                display: "flex",
                gap: 18,
                marginTop: 10,
                fontSize: 12,
                color: "var(--kr-fg-muted)",
                flexWrap: "wrap",
              }}
            >
              {terms?.salary_amount && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <Icon name="Briefcase" size={12} />
                  {terms.salary_amount} {terms.salary_currency || "EUR"}
                </span>
              )}
              {startDate && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <Icon name="Calendar" size={12} />
                  Desde {startDate}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {accountActive && (
            <Badge tone="success" dot>
              Cuenta activa
            </Badge>
          )}
          {showInvite && (
            <Button
              variant="secondary"
              size="sm"
              icon="Mail"
              onClick={onInvite}
              disabled={inviting}
            >
              {inviting
                ? "Enviando…"
                : hasPendingInvite
                  ? "Reenviar invitación"
                  : "Invitar a la plataforma"}
            </Button>
          )}
          <Button variant="primary" size="sm" icon="Pencil">
            Editar
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { tone: "success", label: "Activo" },
    inactive: { tone: "warning", label: "Inactivo" },
    terminated: { tone: "danger", label: "Baja" },
  };
  const m = map[status] || { tone: "neutral", label: status || "—" };
  return (
    <Badge tone={m.tone} dot>
      {m.label}
    </Badge>
  );
}

function PerfilTab({
  profile,
  personal,
  hasContract,
  terms,
  onCompletePersonal,
  onCreateContract,
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {personal ? (
        <Card>
          <SectionTitle eyebrow="Empleado" title="Información personal" />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              marginTop: 14,
            }}
          >
            <ReadOnly label="Email" value={profile?.email} />
            <ReadOnly label="Teléfono" value={profile?.phone} />
            <ReadOnly
              label={
                personal.id_document_type
                  ? `Documento (${personal.id_document_type})`
                  : "Documento"
              }
              value={personal.id_document_number}
            />
            <ReadOnly label="País" value={personal.country} />
            <ReadOnly label="Ciudad" value={personal.city} />
            <ReadOnly label="Teléfono personal" value={personal.personal_phone} />
          </div>
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <Button variant="secondary" size="sm" icon="Pencil" onClick={onCompletePersonal}>
              Editar datos
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon="UserCircle"
            title="Datos personales sin completar"
            description="Aún no se han cargado los datos personales de este empleado. Complétalos para verlos aquí."
            action={{
              label: "Completar datos",
              icon: "Plus",
              onClick: onCompletePersonal,
            }}
          />
        </Card>
      )}

      {hasContract ? (
        <>
          <Card>
            <SectionTitle eyebrow="Empresa" title="Puesto y equipo" />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
                marginTop: 14,
              }}
            >
              <ReadOnly label="Puesto" value={terms?.job_title} />
              <ReadOnly
                label="Salario"
                value={
                  terms?.salary_amount
                    ? `${terms.salary_amount} ${terms.salary_currency || "EUR"}`
                    : null
                }
              />
              <ReadOnly label="Equipo" value="—" hint="Próximamente" />
              <ReadOnly
                label="Manager directo"
                value="—"
                hint="Próximamente"
              />
            </div>
          </Card>

          <Card>
            <SectionTitle eyebrow="Hoy" title="Estado actual de la jornada" />
            <div
              style={{
                marginTop: 14,
                fontSize: 12,
                color: "var(--kr-fg-muted)",
              }}
            >
              Sin datos de marcaje todavía. Disponible cuando integremos jornada.
            </div>
          </Card>
        </>
      ) : (
        <Card>
          <NoContractInline onCreateContract={onCreateContract} context="puesto" />
        </Card>
      )}
    </div>
  );
}

function NoContractTab({ hasContract, onCreateContract, contentTitle, Content }) {
  if (!hasContract) {
    return (
      <Card>
        <EmptyState
          icon="FileText"
          title={`${contentTitle} no disponible`}
          description="Este empleado aún no tiene contrato. Crea uno para activar esta sección."
          action={{
            label: "Ir a Crear un contrato",
            icon: "ArrowRight",
            onClick: onCreateContract,
          }}
        />
      </Card>
    );
  }
  return <Content />;
}

function NoContractInline({ onCreateContract }) {
  return (
    <EmptyState
      icon="Briefcase"
      title="Sin contrato"
      description="Este empleado aún no tiene contrato. Crea uno para mostrar puesto, jornada y ausencias."
      action={{
        label: "Crear contrato",
        icon: "Plus",
        onClick: onCreateContract,
      }}
    />
  );
}

const WEEKDAYS_ORD = [
  ["mon", "Lunes"], ["tue", "Martes"], ["wed", "Miércoles"], ["thu", "Jueves"],
  ["fri", "Viernes"], ["sat", "Sábado"], ["sun", "Domingo"],
];
const SPECIAL_STATUS = {
  pending: { label: "Pendiente", tone: "warning" },
  approved: { label: "Asignada", tone: "success" },
  rejected: { label: "Rechazada", tone: "danger" },
  cancelled: { label: "Cancelada", tone: "neutral" },
};
const unwrapList = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};
const fmtMinAsHM = (min) => `${Math.floor((min || 0) / 60)}h ${(min || 0) % 60 ? `${(min || 0) % 60}m` : ""}`.trim();

function HorarioTab({ employeeProfileId, terms }) {
  const membership = useCurrentMembership();
  const tz = membership.timezone || "Europe/Madrid";
  const canManage = membership.can("time_tracking_corrections:approve");

  const [schedule, setSchedule] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadSpecials = useCallback(async () => {
    if (!employeeProfileId) return;
    try {
      const r = await listSpecialSchedules({ employee_profile_id: employeeProfileId });
      setSpecials(unwrapList(r) || []);
    } catch (e) {
      setSpecials([]);
    }
  }, [employeeProfileId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [schedRes] = await Promise.all([
          terms?.work_schedule_id ? getWorkSchedules().catch(() => null) : Promise.resolve(null),
          loadSpecials(),
        ]);
        if (!alive) return;
        const list = unwrapList(schedRes) || [];
        setSchedule(list.find((s) => s.id === terms?.work_schedule_id) || null);
      } catch (e) {
        if (alive) setError(e?.message || "No se pudo cargar el horario.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [terms, loadSpecials]);

  const removeSpecial = async (row) => {
    if (!window.confirm("¿Retirar esta jornada especial? El empleado volverá a su horario ordinario.")) return;
    setBusyId(row.id);
    try { await deleteSpecialSchedule(row.id); await loadSpecials(); }
    catch (e) { setError(e?.response?.data?.error?.message || e?.message || "No se pudo retirar."); }
    finally { setBusyId(null); }
  };

  const todayYmd = new Date().toISOString().slice(0, 10);
  const days = schedule?.config?.days || {};

  return (
    <>
      <Card>
        <SectionTitle eyebrow="Contrato" title="Jornada ordinaria" />
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={18} /></div>
        ) : !schedule ? (
          <p style={{ marginTop: 12, fontSize: 13, color: "var(--kr-fg-muted)" }}>
            El contrato de este empleado no tiene un horario asignado.
          </p>
        ) : (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{schedule.name}</span>
              {schedule.weekly_minutes ? <Badge tone="neutral" variant="soft">{Math.round(schedule.weekly_minutes / 60)}h/semana</Badge> : null}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {WEEKDAYS_ORD.map(([key, label]) => {
                const d = days[key] || {};
                const tm = d.total_minutes || 0;
                return (
                  <div key={key} style={{ display: "grid", gridTemplateColumns: "110px 1fr", fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--kr-border-subtle)" }}>
                    <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
                    <span style={{ color: tm > 0 ? "var(--kr-fg-strong)" : "var(--kr-fg-subtle)", fontFamily: "var(--kr-font-mono)" }}>
                      {tm > 0 ? `${d.start}–${d.end}${d.break?.minutes ? ` · pausa ${d.break.minutes}m` : ""} · ${fmtMinAsHM(tm)}` : "Descanso"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <SectionTitle eyebrow="Excepciones" title="Jornada especial" />
          {canManage && (
            <Button variant="primary" size="sm" icon="Plus" onClick={() => setShowAssign(true)}>Asignar</Button>
          )}
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--kr-fg-muted)" }}>
          En su rango de fechas, la jornada especial (p. ej. intensiva de verano) tiene prioridad sobre la ordinaria.
        </p>

        {error && <div style={{ marginTop: 12 }}><Alert tone="danger">{error}</Alert></div>}

        {specials.length === 0 ? (
          <p style={{ marginTop: 14, fontSize: 13, color: "var(--kr-fg-subtle)" }}>Sin jornadas especiales asignadas.</p>
        ) : (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            {specials.map((row) => {
              const st = SPECIAL_STATUS[row.status] || { label: row.status, tone: "neutral" };
              const vigente = row.status === "approved" && row.start_date <= todayYmd && todayYmd <= row.end_date;
              return (
                <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 8 }}>
                  <Icon name="CalendarClock" size={18} style={{ color: "var(--kr-fg-muted)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-mono)" }}>
                      {formatYmd(row.start_date)} → {formatYmd(row.end_date)}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                      {row.source === "custom" ? "Horario a medida" : "Horario del catálogo"}{row.note ? ` · ${row.note}` : ""}
                    </div>
                  </div>
                  {vigente && <Badge tone="info" dot>Vigente</Badge>}
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {canManage && (
                    <IconBtn icon="Trash2" title="Retirar" disabled={busyId === row.id} onClick={() => removeSpecial(row)} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <AsignarEspecialModal
        open={showAssign}
        tz={tz}
        employeeProfileId={employeeProfileId}
        onClose={() => setShowAssign(false)}
        onCreated={async () => { setShowAssign(false); await loadSpecials(); }}
      />
    </>
  );
}

function AusenciasTab({ employeeProfileId }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [balance, setBalance] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showFreeDays, setShowFreeDays] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    if (!employeeProfileId) return;
    setLoading(true);
    setError(null);
    try {
      const [bal, abs, adj] = await Promise.all([
        getEmployeeAbsenceBalance(employeeProfileId).catch(() => null),
        getAbsences().catch(() => null),
        getEmployeeAbsenceAdjustments(employeeProfileId).catch(() => null),
      ]);
      setBalance(bal?.data ?? null);
      const list = abs?.data ?? [];
      setAbsences(
        Array.isArray(list)
          ? list.filter((a) => a.employee_profile_id === employeeProfileId)
          : [],
      );
      setAdjustments(Array.isArray(adj?.data) ? adj.data : []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las ausencias.");
    } finally {
      setLoading(false);
    }
  }, [employeeProfileId]);

  useEffect(() => { load(); }, [load]);

  const cycleRange = balance?.cycle_start && balance?.cycle_end
    ? formatAbsenceRange(balance.cycle_start, balance.cycle_end)
    : "Ciclo actual";

  // ¿La concesión aporta saldo al ciclo actual? (mismo criterio que el backend)
  const isUsableInCycle = (adj) => {
    if (!balance?.cycle_start || !balance?.cycle_end) return false;
    const cs = balance.cycle_start;
    const ce = balance.cycle_end;
    const eff = adj.effective_date;
    const exp = adj.expires_at;
    if (!eff || eff > ce) return false;
    return exp ? exp >= cs : eff >= cs;
  };

  // Solo se puede borrar si, al quitar los días, el disponible no queda negativo.
  const canDeleteAdjustment = (adj) => {
    if (!balance || balance.unlimited || !balance.cycle_start) return true;
    const contribution = isUsableInCycle(adj) ? Number(adj.amount) : 0;
    if (contribution <= 0.0001) return true;
    const availableWithout =
      Number(balance.allowance || 0) +
      (Number(balance.adjustments || 0) - contribution) -
      Number(balance.used || 0) -
      Number(balance.pending || 0);
    return availableWithout >= -0.0001;
  };

  const onDeleteAdjustment = async (adj) => {
    const ok = await confirm({
      title: "Eliminar días concedidos",
      description: `Se retirarán ${formatAbsenceDays(adj.amount)} del saldo de vacaciones. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    setDeletingId(adj.id);
    try {
      await deleteEmployeeAbsenceAdjustment(employeeProfileId, adj.id);
      toast.success("Concesión eliminada");
      await load();
    } catch (err) {
      toast.error(
        "No se pudo eliminar",
        typeof err === "string" ? err : err?.message || "El saldo disponible quedaría negativo.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <SectionTitle eyebrow="Saldo de vacaciones" title={cycleRange} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" size="sm" icon="Gift" onClick={() => setShowFreeDays(true)}>
              Agregar días libres
            </Button>
            <Button variant="primary" size="sm" icon="Plus" onClick={() => setShowNew(true)}>
              Añadir ausencia
            </Button>
          </div>
        </div>
        {balance ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginTop: 14 }}>
            <BalanceTile label="Disponible" value={balance.unlimited ? "∞" : numberOrDash(balance.available)} primary />
            <BalanceTile label="Concedidos" value={balance.unlimited ? "∞" : numberOrDash(balance.adjustments)} />
            <BalanceTile label="Disfrutados" value={numberOrDash(balance.taken)} />
            <BalanceTile label="Aprobadas" value={numberOrDash(balance.approved_not_taken)} />
          </div>
        ) : (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--kr-fg-muted)" }}>
            Sin información de saldo (¿el contrato no tiene política asignada?).
          </div>
        )}
      </Card>

      {error && <Alert tone="danger">{error}</Alert>}

      {adjustments.length > 0 && (
        <Card padded={false}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
            <SectionTitle eyebrow="Días libres concedidos" title={`Por convenio (${adjustments.length})`} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {adjustments.map((adj, i) => {
              const deletable = canDeleteAdjustment(adj);
              const isDeleting = deletingId === adj.id;
              return (
                <div
                  key={adj.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "64px 1fr auto",
                    gap: 12,
                    alignItems: "start",
                    padding: "12px 20px",
                    borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)",
                    fontSize: 13,
                  }}
                >
                  <span style={{ fontFamily: "var(--kr-font-mono)", fontWeight: 600, color: "var(--kr-success-500)" }}>
                    +{formatAbsenceDays(adj.amount)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "var(--kr-fg-strong)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {adj.note || "—"}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "var(--kr-fg-muted)" }}>
                      Desde {formatAbsenceRange(adj.effective_date)}
                      {adj.expires_at
                        ? ` · caduca ${formatAbsenceRange(adj.expires_at)}`
                        : " · vigente el ciclo actual"}
                    </div>
                  </div>
                  <IconBtn
                    icon={isDeleting ? "Loader" : "Trash2"}
                    title={
                      deletable
                        ? "Eliminar concesión"
                        : "No se puede eliminar: el disponible quedaría negativo (el empleado ya disfrutó/solicitó esos días)"
                    }
                    disabled={!deletable || isDeleting}
                    onClick={() => onDeleteAdjustment(adj)}
                  />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--kr-border-subtle)" }}>
          <SectionTitle eyebrow="Histórico" title={`Solicitudes (${absences.length})`} />
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={16} /></div>
        ) : absences.length === 0 ? (
          <EmptyState
            icon="Calendar"
            title="Sin ausencias todavía"
            description="Cuando el empleado solicite o se le añadan ausencias aparecerán aquí."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {absences.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.4fr 60px 110px",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 20px",
                  borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)",
                  fontSize: 13,
                }}
              >
                <Badge tone={ABSENCE_TYPE_TONE[a.type]}>{ABSENCE_TYPE_LABEL[a.type]}</Badge>
                <span style={{ color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-mono)" }}>
                  {formatAbsenceRange(a.start_date, a.end_date)}
                </span>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                  {formatAbsenceDays(a.days_count)}
                </span>
                <Badge tone={ABSENCE_STATUS_TONE[a.status]} dot>
                  {ABSENCE_STATUS_LABEL[a.status]}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      <NewAbsenceModal
        open={showNew}
        employeeProfileId={employeeProfileId}
        onClose={() => setShowNew(false)}
        onCreated={() => { setShowNew(false); load(); }}
      />

      <AddFreeDaysModal
        open={showFreeDays}
        employeeProfileId={employeeProfileId}
        onClose={() => setShowFreeDays(false)}
        onCreated={() => { setShowFreeDays(false); load(); }}
      />
    </div>
  );
}

function BalanceTile({ label, value, primary }) {
  return (
    <div style={{ padding: 12, background: "var(--kr-neutral-50)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{label}</div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontSize: 22,
          fontWeight: 700,
          color: primary ? "var(--kr-action)" : "var(--kr-fg-strong)",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function numberOrDash(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function ContratoTab({ contract, terms }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <SectionTitle eyebrow="Vigente" title={terms?.job_title || "Contrato"} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginTop: 14,
          }}
        >
          <ReadOnly label="Inicio" value={formatYmd(contract?.start_date)} />
          <ReadOnly
            label="Fin"
            value={formatYmd(contract?.end_date) || "Indefinido"}
          />
          <ReadOnly label="Puesto" value={terms?.job_title} />
          <ReadOnly
            label="Salario"
            value={
              terms?.salary_amount
                ? `${terms.salary_amount} ${terms.salary_currency || "EUR"}`
                : null
            }
          />
          <ReadOnly
            label="Vigencia condiciones"
            value={
              terms?.effective_from
                ? `${formatYmd(terms.effective_from)}${terms.effective_to ? ` → ${formatYmd(terms.effective_to)}` : ""}`
                : null
            }
          />
          <ReadOnly
            label="Tipo de cambio"
            value={terms?.change_type}
          />
        </div>
      </Card>
    </div>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--kr-fg-muted)",
          fontWeight: 600,
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--kr-fg-strong)",
          marginTop: 4,
        }}
      >
        {title}
      </div>
    </div>
  );
}

function ReadOnly({ label, value, hint, avatarName }) {
  const display = value || "—";
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: value ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
          fontWeight: value ? 500 : 400,
        }}
      >
        {avatarName && <Avatar name={avatarName} size={20} />}
        {display}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export default EmpleadoDetalleDrawer;
